/**
 * Recursos (feature flags) liberados para a conta logada.
 *
 * A lista vem de GET v2/auth/me e fica em localStorage, do mesmo jeito que
 * `perms`. O router guard chama `ensure_features()` antes de cada navegação,
 * então a leitura síncrona (`has_feature`) já encontra o cache preenchido em
 * qualquer componente — inclusive dentro de `data()`, que roda antes de
 * `mounted`.
 */

// Nova tela de cotações (/cliente/monitores/cotacoes-v2).
export const REQUESTS_V2 = 'requests.v2';
// Novo fluxo de vencedores (/cliente/monitores/vencedores-v2/:id).
export const WINNERS_V2 = 'winners.v2';
// Nova tela de fornecedores (/fornecedores-v2).
export const SELLERS_V2 = 'sellers.v2';
// Nova tela de produtos (/produtos-v2).
export const PRODUCTS_V2 = 'products.v2';
// Visitas dos vendedores (/visitas).
export const VISITS = 'visits';
// Requisições dos vendedores (/requisicoes).
export const REQUISITIONS = 'requisitions';
// Pedido parcial (CLUB-1443): pedir parte dos vencedores e manter a cotação
// aberta para os vendedores continuarem digitando o que ainda não foi pedido.
export const PARTIAL_ORDERS = 'requests.partial_orders';

const STORAGE_KEY = 'features';

let inflight = null;

/** Lista em cache; nunca lança, cache corrompido é descartado. */
export function cached_features() {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch (_error) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
    }
}

/** Leitura síncrona. Sem cache responde `false` — o padrão é o fluxo antigo. */
export function has_feature(key) {
    return (cached_features() || []).some((feature) => feature.key === key);
}

/**
 * Garante o cache preenchido. Uma requisição por sessão: chamadas concorrentes
 * compartilham a mesma promise, e sem token nem tenta.
 */
export function ensure_features() {
    const cached = cached_features();

    if (cached) return Promise.resolve(cached);

    if (!localStorage.getItem('token')) return Promise.resolve([]);

    if (!inflight) {
        // Import tardio de propósito: auth.service -> http -> store ->
        // ErrorHandlerService -> routes -> páginas -> feature.mixin -> aqui.
        // Com o import no topo, quem carregasse este arquivo antes do router
        // (ex.: o MobileMenu, pelo App.vue) via a aba cotacoes-v2 ser definida
        // com `feature_mixin` ainda undefined, e o refresh nela quebrava em
        // "Cannot read properties of undefined (reading 'props')".
        inflight = import('@/services/v3/auth.service')
            .then(({ default: AuthenticationService }) => new AuthenticationService().me())
            .then((response) => {
                const features = Array.isArray(response.data?.features)
                    ? response.data.features
                    : [];

                localStorage.setItem(STORAGE_KEY, JSON.stringify(features));
                return features;
            })
            .catch(() => [])
            .finally(() => {
                inflight = null;
            });
    }

    return inflight;
}

/**
 * Descarta o cache. Obrigatório ao trocar de token: os recursos resolvidos
 * valem para o usuário anterior.
 */
export function clear_features() {
    localStorage.removeItem(STORAGE_KEY);
    inflight = null;
}
